import {
  ViewingsDispatchLifecycleStatus,
  ViewingsDispatchRequest,
} from "@/schemas";

function nowIso(): string {
  return new Date().toISOString();
}

function withTimeline(
  request: ViewingsDispatchRequest,
  actorName: string,
  action: string,
): ViewingsDispatchRequest {
  return {
    ...request,
    activityTimeline: [
      {
        id: `tl_${request.id}_${request.activityTimeline.length + 1}`,
        at: nowIso(),
        actorName,
        action,
      },
      ...request.activityTimeline,
    ],
  };
}

export function acceptDate(
  request: ViewingsDispatchRequest,
  actorName: string,
  date: string,
): ViewingsDispatchRequest {
  return withTimeline(
    {
      ...request,
      lifecycleStatus: "accepted",
      acceptedDate: date,
      flags: {
        ...request.flags,
        waitingOver48h: false,
        acceptedAfterDeadline: Boolean(
          request.deadlineDate && date > request.deadlineDate,
        ),
      },
    },
    actorName,
    "viewings.timeline.acceptedDate",
  );
}

export function acceptOffer(
  request: ViewingsDispatchRequest,
  actorName: string,
): ViewingsDispatchRequest {
  return withTimeline(
    {
      ...request,
      lifecycleStatus: "accepted",
      flags: {
        ...request.flags,
        waitingOver48h: false,
      },
    },
    actorName,
    "viewings.timeline.acceptedDate",
  );
}

export function submitViewingDate(
  request: ViewingsDispatchRequest,
  actorName: string,
  date: string,
): ViewingsDispatchRequest {
  return withTimeline(
    {
      ...request,
      lifecycleStatus: "scheduled",
      acceptedDate: date,
      flags: {
        ...request.flags,
        waitingOver48h: false,
        acceptedAfterDeadline: Boolean(
          request.deadlineDate && date > request.deadlineDate,
        ),
      },
    },
    actorName,
    "viewings.timeline.submittedViewingDate",
  );
}

export function declineAndRedispatch(
  request: ViewingsDispatchRequest,
  actorName: string,
): ViewingsDispatchRequest {
  return withTimeline(
    {
      ...request,
      lifecycleStatus: "declined",
      flags: {
        ...request.flags,
        noMatch: true,
      },
    },
    actorName,
    "viewings.timeline.declinedAssignment",
  );
}

export function markScheduled(
  request: ViewingsDispatchRequest,
  actorName: string,
): ViewingsDispatchRequest {
  return withTimeline(
    {
      ...request,
      lifecycleStatus: "scheduled",
    },
    actorName,
    "viewings.timeline.markedReady",
  );
}

export function simulateInvalidDateReply(
  request: ViewingsDispatchRequest,
  actorName: string,
): ViewingsDispatchRequest {
  const nextInvalidResponseCount =
    (request.outreach?.invalidResponseCount || 0) + 1;

  return withTimeline(
    {
      ...request,
      outreach: request.outreach
        ? {
            ...request.outreach,
            dateReplyStatus: "invalid_reprompt_sent",
            invalidResponseCount: nextInvalidResponseCount,
            dispatcherNotificationStatus:
              nextInvalidResponseCount > 1
                ? "notified_invalid_response"
                : request.outreach.dispatcherNotificationStatus,
          }
        : request.outreach,
    },
    actorName,
    "viewings.timeline.invalidDateReply",
  );
}

export function getLifecycleBadgeClassName(
  status: ViewingsDispatchLifecycleStatus,
): string {
  const map: Record<ViewingsDispatchLifecycleStatus, string> = {
    pending: "bg-muted-background text-muted-foreground",
    suggested: "bg-categories-gray-50 text-categories-gray-900",
    contacted: "bg-alert-warning-100 text-alert-warning-900",
    accepted: "bg-alert-success-100 text-alert-success-900",
    noMatch: "bg-alert-error-100 text-alert-error-900",
    scheduled: "bg-categories-gray-50 text-categories-gray-900",
    completed: "bg-alert-success-100 text-alert-success-900",
    completedRemotely: "bg-alert-success-100 text-alert-success-900",
    onHold: "bg-muted-background text-muted-foreground",
    declined: "bg-alert-error-100 text-alert-error-900",
    needsAction: "bg-categories-red-50 text-categories-red-900",
    cancelled: "bg-muted-background text-muted-foreground",
  };

  return `rounded px-2 py-1 text-xs font-medium ${map[status]}`;
}

export const VIEWINGS_DISPATCH_FLAG_CLASSNAMES = {
  noMatch: "rounded bg-alert-error-100 px-2 py-1 text-alert-error-900",
  waitingOver48h:
    "rounded bg-alert-warning-100 px-2 py-1 text-alert-warning-900",
  acceptedAfterDeadline:
    "rounded bg-alert-error-100 px-2 py-1 text-alert-error-900",
  reassignedInvalidation:
    "rounded bg-categories-lavender-50 px-2 py-1 text-categories-lavender-900",
} as const;
